/**
 * dst-header-scroll — toggles the header from transparent+scrim to a solid
 * backing once the page has scrolled past a threshold, and maintains
 * --header-height (consumed by dst-buy-panel.liquid's "side-right" position
 * variant — Dawn's stock <sticky-header> used to set this, but that element
 * is stock-only and no longer rendered now that dst-header replaces it).
 *
 * The scroll toggle is deliberately not built on window.DstScrollUtils.
 * ScrollGate (see scroll-utils.js) — that's a continuous, rAF-throttled
 * per-frame callback meant for scroll-linked animation (frame sequences,
 * parallax). This is a one-time boolean flip on an element that's always in
 * the viewport (it's sticky), so a plain IntersectionObserver watching a 1px
 * sentinel at the threshold offset is cheaper and simpler: it only fires
 * when the threshold is actually crossed, no scroll listener or rAF loop
 * running the whole time the visitor is on the page.
 */
class DstHeaderScroll extends HTMLElement {
  connectedCallback() {
    this._setHeaderHeight();
    this._resizeHandler = () => this._setHeaderHeight();
    window.addEventListener('resize', this._resizeHandler);

    if (this.dataset.solidOnScroll !== 'true') return;

    const threshold = parseInt(this.dataset.scrollThreshold, 10) || 80;

    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = `position:absolute; top:${threshold}px; left:0; width:1px; height:1px; pointer-events:none;`;
    document.body.prepend(sentinel);
    this._sentinel = sentinel;

    this._observer = new IntersectionObserver(([entry]) => {
      this.classList.toggle('dst-header--solid', !entry.isIntersecting);
    });
    this._observer.observe(sentinel);
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    if (this._sentinel) this._sentinel.remove();
    if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
  }

  _setHeaderHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
  }
}

customElements.define('dst-header-scroll', DstHeaderScroll);
